import { useState, useCallback, useEffect, useRef } from "react";
import { useRecoilValue } from "recoil";
import {
  SellerMemberDto,
  getMemberDashboardListSort,
  SellerMemberResponse,
  getMemberSearch,
  joinMemberApi,
} from "../../../../api/member";
import { PaginationWrapper } from "../../../../component/CommonTable";
import PopupPagination from "../../../../component/Pagination/popupPagination";
import MobileHeader from "../../../../layout/mobile-header";
import { pubState } from "../../../../recoil/pub.atom";
import {
  MobileMemberListWrapper,
  MobileMemberListSearch,
  MobileMemberTableWrapper,
  MobileMemberTableHeader,
  MobileMemberTableBody,
  MobileMemberTableBodyItem,
  MobileMemberWrapper,
  MobileMemberSearchButton,
  MobileMemberSearchButtonWrapper,
  MobileMemberSearchInputWrapper,
  MobileMemberSearchResultWrapper,
  MobileMemberSearchWrapper,
} from "./style";
import { Title } from "../../../../styles/page";
import { SearchMemberInfo } from "../../style";
import { exchangePoint } from "../../../../utils/number";

const MobileMember = () => {
  const [list, setList] = useState<SellerMemberDto[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [searchMember, setSearchMember] = useState<string>("");
  const pub = useRecoilValue(pubState);
  const limit = 7;
  const [searchNickname, setSearchNickname] = useState<string>("");
  const [joinInfo, setJoinInfo] = useState<string>("");
  const [isRunnerPub, setIsRunnerPub] = useState<boolean>(false);
  const [realNickname, setRealNickname] = useState<string>("");
  const [searchPhone, setSearchPhone] = useState<string>("");
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [isSearchNone, setIsSearchNone] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleSearch = async () => {
    if (!pub) return;
    if (!searchNickname || !searchPhone) {
      alert("모든 필드를 채워주세요. ");
      return;
    }
    const filterNickname = searchNickname.replace(/\s+/g, "");

    try {
      const res = await getMemberSearch(filterNickname, searchPhone, pub.id);
      setIsRunnerPub(res.payload.alreadyMember); // 러너펍 방문 여부
      setIsSearch(true);
      setIsSearchNone(false);
      setJoinInfo("");
      setRealNickname(res.payload.nickname);
      if (res.payload.isPubMember) {
        setJoinInfo("joined");
        return;
      }
    } catch (e: any) {
      setIsSearchNone(true);
      return;
    }
  };

  const handleSearchNone = () => {
    setSearchNickname("");
    setSearchPhone("");
    setIsSearch(false);
    setIsSearchNone(false);
  };

  const handleJoinMember = async () => {
    if (!pub) return;

    if (joinInfo === "joined") {
      return;
    }

    const req = {
      nickname: realNickname,
      phoneNumber: searchPhone,
      pubId: pub.id,
    };
    try {
      const res = await joinMemberApi(req);
      alert(res.message);
      setIsSearch(false);
      setSearchNickname("");
      setSearchPhone("");
      setJoinInfo("");
      getList();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const onPageChange = async (nextPage: number) => {
    setCurrentPage(nextPage);
  };

  const getList = useCallback(() => {
    if (!pub) return;
    try {
      getMemberDashboardListSort(currentPage, limit, pub.id, searchMember).then(
        (res) => setData(res),
      );
    } catch (e) {
      console.error(e);
    }
  }, [pub, currentPage, searchMember]);

  const onSearchClick = () => {
    getList();
    onPageChange(1);
  };

  const setData = (res: SellerMemberResponse) => {
    setList(res.list);
    setTotalPage(Math.max(1, Math.ceil(res.total / limit)));
  };

  useEffect(() => {
    getList();
  }, [getList]);

  const formatDate = (dateTime: string, time: boolean = false) => {
    if (!dateTime) return null;

    if (time) return dateTime.split(" ")[1];

    const date = dateTime.split("T")[0];
    const [yyyy, mm, dd] = date.split("-");

    return `${yyyy}/${mm}/${dd}`;
  };

  const headers = [
    {
      header: "닉네임",
      name: "name",
      render: (item: SellerMemberDto) => item.nickname,
    },
    {
      header: "가입날짜",
      name: "date",
      render: (item: SellerMemberDto) => formatDate(item.createdAt),
    },
    {
      header: "참여횟수",
      name: "count",
      render: (item: SellerMemberDto) => item.count?.toLocaleString(),
    },
    {
      header: "RB",
      name: "count",
      render: (item: SellerMemberDto) => item.seed?.toLocaleString(),
    },
    {
      header: "CR",
      name: "count",
      render: (item: SellerMemberDto) => (item.carrot ?? 0).toLocaleString(),
    },
    {
      header: "E",
      name: "count",
      render: (item: SellerMemberDto) => exchangePoint(item.point),
    },
  ];

  return (
    <MobileMemberWrapper>
      <MobileHeader title="모바일 셀러" />
      <MobileMemberSearchWrapper>
        <Title weight={700} size={18}>
          회원 검색
        </Title>
        {!isSearch ? (
          <MobileMemberSearchInputWrapper>
            <input
              type="text"
              value={searchNickname}
              onChange={(e) =>
                setSearchNickname(e.target.value.replace(/\s+/g, ""))
              }
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  inputRef.current?.focus();
                }
              }}
              placeholder="닉네임을 입력 해주세요."
            />
            <input
              type="text"
              value={searchPhone}
              onChange={(e) => {
                if (e.target.value.length > 4) {
                  return;
                }
                setSearchPhone(e.target.value.replace(/[^0-9]/g, ""));
              }}
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  handleSearch();
                }
              }}
              ref={inputRef}
              placeholder="전화번호 뒷 4자리를 입력 해주세요."
            />
            {isSearchNone && (
              <div className="warning">*등록된 유저가 없습니다. </div>
            )}
            <MobileMemberSearchButtonWrapper>
              <MobileMemberSearchButton
                isGray={
                  searchNickname && searchPhone.length === 4 ? false : true
                }
                onClick={() => handleSearch()}
              >
                검색
              </MobileMemberSearchButton>
            </MobileMemberSearchButtonWrapper>
          </MobileMemberSearchInputWrapper>
        ) : (
          <MobileMemberSearchResultWrapper>
            <div className="box">{searchNickname}</div>
            <div className="box">{searchPhone}</div>
            <SearchMemberInfo isRed={joinInfo === "joined"}>
              *
              {joinInfo === "joined" ? (
                "이미 가입된 유저입니다. "
              ) : (
                <>
                  회원가입이 가능한 유저입니다.
                  <br />
                  *러너펍 첫 방문: {isRunnerPub ? "X" : "O"}
                </>
              )}
            </SearchMemberInfo>
            <MobileMemberSearchButtonWrapper>
              <MobileMemberSearchButton
                isGray={joinInfo === "joined"}
                onClick={() => handleJoinMember()}
              >
                가입 승인
              </MobileMemberSearchButton>
              <div className="cancel-button" onClick={() => handleSearchNone()}>
                취소
              </div>
            </MobileMemberSearchButtonWrapper>
          </MobileMemberSearchResultWrapper>
        )}
      </MobileMemberSearchWrapper>

      <MobileMemberListWrapper>
        <Title size={18} weight={700}>
          매장 회원 리스트
        </Title>
        <MobileMemberListSearch>
          <input
            value={searchMember}
            onChange={(e) => setSearchMember(e.target.value)}
            placeholder="닉네임을 검색하세요."
          />
          <img
            src="/image-web/timer-config/searchIcon.svg"
            alt="search"
            onClick={onSearchClick}
          />
        </MobileMemberListSearch>
        <div className="bar" />
        <MobileMemberTableWrapper>
          <MobileMemberTableHeader>
            {headers.map((header, i) => (
              <div key={i} className={`item ${header.name}`}>
                {header.header}
              </div>
            ))}
          </MobileMemberTableHeader>
          <MobileMemberTableBody>
            {list.map((item, i) => (
              <MobileMemberTableBodyItem key={i}>
                {headers.map((header, i) => (
                  <div key={i} className={`item ${header.name}`}>
                    {header.render(item)}
                  </div>
                ))}
              </MobileMemberTableBodyItem>
            ))}
          </MobileMemberTableBody>
        </MobileMemberTableWrapper>
        <PaginationWrapper>
          <PopupPagination
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            totalPages={totalPage}
          />
        </PaginationWrapper>
      </MobileMemberListWrapper>
    </MobileMemberWrapper>
  );
};

export default MobileMember;
